import { accountIdToEthereumAddress } from '@chainflip/delegation';
import { hexToBytes } from '@chainflip/utils/bytes';
import { type AnyChainflipChain, isAnyChainflipChain } from '@chainflip/utils/chainflip';
import { type HexString } from '@chainflip/utils/types';

export const toNumber = (string: string): number | typeof NaN => parseInt(string, 10);

export const isIdSs58 = (value: string): boolean => value.startsWith('cF') && value.length === 49;

export const isValidValidatorHex = (text: string): boolean => {
  if (text) {
    return text.slice(0, 2) === '0x' && text.length === 66;
  }
  return false;
};

export const formatIpfsUrl = (stringURI: string) =>
  stringURI.startsWith('ipfs') ? `https://ipfs.io/ipfs/${stringURI.slice(7)}` : stringURI;

export const hexToUtf8 = (hex: HexString): string => {
  const bytes = hexToBytes(hex);
  return new TextDecoder().decode(bytes);
};

const isChainflipChain = (value: string): value is AnyChainflipChain => isAnyChainflipChain(value);

export const parseChannelId = (id: string | string[] | undefined) => {
  if (typeof id !== 'string') return null;

  const matches = /^(\d+)-(\w+)-(\d+)$/.exec(id.toUpperCase());

  if (!matches) return null;

  const [, issuedBlockIdString, chain, channelId] = matches;
  const sourceChain = `${chain[0].toUpperCase()}${chain.slice(1).toLowerCase()}`;

  const issuedBlockId = Number.parseInt(issuedBlockIdString, 10);

  if (Number.isNaN(issuedBlockId)) return null;

  if (!isChainflipChain(sourceChain)) return null;

  return { issuedBlockId, sourceChain, channelId };
};

export const matchCaseInsensitive = (text: string, pattern: string): boolean =>
  // need to escape special characters
  new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i').test(text);

export const removeCommas = (value: string): string => value.replace(/,/g, '');

export const formatRuntimeVersion = (value: number | undefined): string => {
  if (typeof value === 'undefined') return '';
  const string = value.toString();
  const partLength = Math.ceil(string.length / 3);
  const padded = string.padStart(partLength * 3, '0');
  const major = Number(padded.slice(0, partLength));
  const minor = Number(padded.slice(partLength, partLength * 2));
  const patch = Number(padded.slice(partLength * 2, partLength * 3));
  return `v${major}.${minor}.${patch}`;
};

export const endsWith = <T extends string, const U extends string>(
  str: T | undefined,
  suffix: U,
): str is Extract<T, `${string}${U}`> => str !== undefined && str.endsWith(suffix);

export const formatBps = (value: number) => `${(value / 100).toFixed(2)}%`;
export const formatPct = (value: number) => `${(value * 100).toFixed(2)}%`;
export const displayPct = (value: number, decimals = 2) => `${value.toFixed(decimals)}%`;

export const padHexBytes = (value: HexString, byteLength: number, padWith = '0'): HexString =>
  `0x${value.slice(2).padStart(byteLength * 2, padWith)}`;

const isEvmCfAccount = (address: string): address is `cFHsUq1uK5opJudRD${string}` =>
  address.startsWith('cFHsUq1uK5opJudRD');

export const getSourceAddress = (idSs58: string): string | null => {
  try {
    return isEvmCfAccount(idSs58) ? accountIdToEthereumAddress(idSs58) : null;
  } catch {
    return null;
  }
};
